import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';

// Extension to handle {{survey.link}} replacement
export const SurveyLinkReplacement = Extension.create({
  name: 'surveyLinkReplacement',

  addOptions() {
    return {
      initialSurveyId: null,
    };
  },

  addProseMirrorPlugins() {
    // Resolved on use, and a getter is accepted, so a host can keep one stable
    // extension array while the picked survey changes. Rebuilding the array to
    // hand over a new id remounts the editor, which reloads the body and drops
    // the survey the user just picked (ENG-3826).
    const readInitialSurveyId = () => {
      const option = this.options.initialSurveyId;
      return typeof option === 'function' ? option() : option;
    };

    // Shared by `appendTransaction` (live edits) and the plugin's `view` init
    // (content the editor was CREATED with — see below). Collects every match
    // against the untouched doc first: editing inside the walk shifted the doc
    // under it — `{{survey.link}}` is 15 chars and the node replacing it is 1 —
    // so every later position was off by 14 and eventually ran past the end
    // ("Position N out of range").
    const convertPlaceholders = (doc, tr, schema) => {
      const matches = [];
      doc.descendants((node, pos) => {
        if (!node.isText) return;
        const text = node.text || '';
        const regex = /\{\{survey\.link\}\}/g;
        let match;
        while ((match = regex.exec(text)) !== null) {
          matches.push({
            from: pos + match.index,
            to: pos + match.index + match[0].length,
          });
        }
      });

      if (matches.length === 0) return false;

      // Last match first, so each replacement leaves the earlier offsets valid.
      for (let i = matches.length - 1; i >= 0; i--) {
        const { from, to } = matches[i];
        tr.replaceWith(
          from,
          to,
          schema.nodes.surveySelect.create({
            surveyId: readInitialSurveyId() ?? null,
          })
        );
      }

      return true;
    };

    return [
      new Plugin({
        key: new PluginKey('surveyLinkReplacement'),

        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, oldDecoSet) {
            return oldDecoSet.map(tr.mapping, tr.doc);
          },
        },

        appendTransaction(transactions, oldState, newState) {
          const docChanged = transactions.some((tr) => tr.docChanged);
          if (!docChanged) return null;

          const { doc, tr, schema } = newState;
          return convertPlaceholders(doc, tr, schema) ? tr : null;
        },

        // ProseMirror only calls `appendTransaction` for transactions
        // dispatched AFTER the view exists — content the editor was created
        // with (a saved rejection config, or a template picked before this
        // instance mounted) never passes through it. A remount is the same
        // case: kula's Editor recreates its Tiptap instance on some prop
        // changes (ENG-3826), so a body that already contains
        // `{{survey.link}}` needs converting once here too, or the token sits
        // as raw text with no dropdown to pick a survey from at all — and the
        // still-raw `{{survey.link}}` reads as an unresolved template
        // variable to the generic invalid-variable check instead of the
        // survey-specific "select a survey" one.
        view(editorView) {
          const { state } = editorView;
          const tr = state.tr;
          if (convertPlaceholders(state.doc, tr, state.schema)) {
            editorView.dispatch(tr);
          }
          return {};
        },
      }),
    ];
  },
});
